import React, { useState } from 'react';
import { ScanResult } from '@capacitor-community/barcode-scanner';
import { Center, Heading, Image, ListItem, Text, UnorderedList, useToast } from '@chakra-ui/react';
import { CapacitorWifiConnect, ConnectState } from '@falconeta/capacitor-wifi-connect';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import phoneScanImg from '../../../../../assets/images/mobile_qr_add.svg';
import { ScanBarcodeButton } from '../../../../../common/components/ScanBarcodeButton';
import { SlidingAnimationPageBase } from '../../../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../../../common/hooks/use-param-back-url';
import { timeout } from '../../../../../utils/common/common-utils';
import { extractWifiCredentials } from '../../../../account/hardware/helpers';
import { useOnboardingWizardProgress } from '../../../../home/useOnboardingWizardProgress';
import { WAIT_FOR_NATIVE_WIFI_MS } from './constants';

export function WifiDeviceStart() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const { needsToConfigureHardware } = useOnboardingWizardProgress();
  const [isLoading, setLoading] = useState(false);

  const fallbackBackURL = needsToConfigureHardware
    ? '../../../first_time_hardware_configuration?direction=back'
    : '../../../devices/add?direction=back';
  const backURL = useParamBackUrl(fallbackBackURL);

  /* istanbul ignore next -- @preserve */
  async function handleScannedBarcode(scanResult: ScanResult) {
    try {
      if (scanResult.content) {
        setLoading(true);
        const { ssid, password } = extractWifiCredentials(scanResult.content);

        const connected = await CapacitorWifiConnect.secureConnect({
          ssid,
          password,
        });

        if (connected.value === ConnectState.Ok) {
          await timeout(WAIT_FOR_NATIVE_WIFI_MS); // Wait a bit to give the device time to connect if just connected
          navigate('../local-setup');
        } else {
          const errorMessage = getWifiConnectionErrorMessage(connected.value, t);
          toast({
            title: t('Set Up Hardware.error connecting to wifi'),
            description: errorMessage,
            status: 'error',
          });
        }
      }
    } catch (e) {
      toast({
        title: t('Common.invalid qr code'),
        description: t('Common.please try again'),
        status: 'error',
      });
    }
    setLoading(false);
  }

  return (
    <SlidingAnimationPageBase title={t('Common.device setup')} backURL={backURL}>
      <Center flexDirection="column" textAlign="center" mx={6}>
        <Image h={200} src={phoneScanImg} alt="add device qr" />
        <Heading size="lg" my={4}>
          {t('Devices.lets connect your wifi device')}
        </Heading>
        <Text mr="auto">{t('Devices.you will require')}:</Text>
        <UnorderedList textAlign="left" mb={3} mr="auto">
          <ListItem>{t('Devices.wifi required')}</ListItem>
          <ListItem>{t('Devices.device wap is activated')}</ListItem>
        </UnorderedList>
        <Text fontWeight={600} size="large">
          {t('Devices.important dont connect')}
        </Text>

        <ScanBarcodeButton
          onScanError={
            /* istanbul ignore next -- @preserve */
            (error) => {
              console.error(error);
            }
          }
          onScan={handleScannedBarcode}
          data-testid={'scan-qr-button'}
          mt={8}
          w={'80%'}
          isDisabled={isLoading}
          isLoading={isLoading}
        />
      </Center>
    </SlidingAnimationPageBase>
  );
}

/* istanbul ignore next -- @preserve */
function getWifiConnectionErrorMessage(connectState: ConnectState, t: TFunction) {
  let errorMessage = t('Common.please try again');
  switch (connectState) {
    case ConnectState.Denied:
      errorMessage = t('Set Up Hardware.access denied');
      break;
    case ConnectState.UnknowSsid:
      errorMessage = t('Set Up Hardware.unknown ssid');
      break;
    case ConnectState.WifiDisabled:
      errorMessage = t('Set Up Hardware.wifi disabled');
      break;
    case ConnectState.AppLocalizationPermission:
      errorMessage = t('Set Up Hardware.app localization denied');
      break;
    case ConnectState.SystemLocalizationPermission:
      errorMessage = t('Set Up Hardware.system localization denied');
      break;
    default:
      break;
  }
  return errorMessage;
}
